import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores'
import { useEffect, useState } from 'react'
import './LoginPage.css'

// Detect if running in a WebView (in-app browser)
function isWebView(): boolean {
  const ua = navigator.userAgent || navigator.vendor || ''

  // iOS WebView
  if (/iPhone|iPod|iPad/.test(ua)) {
    // Safari has "Safari" in UA, WebView doesn't
    if (!/Safari/.test(ua)) return true
    // LINE, Facebook, Instagram in-app browsers
    if (/FBAN|FBAV|Line|Instagram/.test(ua)) return true
  }

  // Android WebView
  if (/Android/.test(ua)) {
    // LINE, Facebook, Instagram in-app browsers
    if (/FBAN|FBAV|Line|Instagram|wv/.test(ua)) return true
  }

  return false
}

export default function LoginPage() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [showWebViewWarning, setShowWebViewWarning] = useState(false)

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard')
    }
  }, [isAuthenticated, navigate])

  useEffect(() => {
    setShowWebViewWarning(isWebView())
  }, [])

  const handleGoogleLogin = () => {
    const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID
    const redirectUri = encodeURIComponent(window.location.origin + '/callback')
    const responseType = 'code'
    const scope = encodeURIComponent('email openid profile')

    if (cognitoDomain && clientId) {
      window.location.href = `${cognitoDomain}/oauth2/authorize?client_id=${clientId}&response_type=${responseType}&scope=${scope}&redirect_uri=${redirectUri}&identity_provider=Google`
    } else {
      console.log('Development mode: OAuth not configured')
    }
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.origin)
    alert('Link copied! Please open in Safari or Chrome.')
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1 className="login-logo">Branka</h1>
          <p className="login-tagline">Craft your ideas.</p>
        </div>

        <div className="login-content">
          {showWebViewWarning ? (
            <>
              <div className="webview-warning">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <p>
                  Google sign-in is not supported in this browser. Please open in <strong>Safari</strong> or <strong>Chrome</strong>.
                </p>
              </div>

              <button className="btn-copy-link" onClick={handleCopyLink}>
                Copy link to clipboard
              </button>

              <p className="webview-hint">
                Tap ••• or share button, then "Open in Safari/Chrome"
              </p>
            </>
          ) : (
            <>
              <p className="login-description">
                A branching-first note system that lets your ideas grow into a living network of
                thought.
              </p>

              <button className="btn-google" onClick={handleGoogleLogin}>
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>
            </>
          )}
        </div>

        <div className="login-footer">
          <p className="login-privacy">
            By continuing, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  )
}
